import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../ui/carousel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTOPLAY_INTERVAL = 2500;
const CAROUSEL_OPTS = { loop: true, align: "start" };

export const PhotoCarousel = () => {
  const { lang } = useLanguage();
  const [photos, setPhotos] = useState(null);
  const [emblaApi, setEmblaApi] = useState(null);
  const [paused, setPaused] = useState(false);
  const autoplayRef = useRef(null);

  useEffect(() => {
    api.get("/photos", { params: { section: "carousel" } }).then(({ data }) => setPhotos(data)).catch(() => setPhotos([]));
  }, []);

  useEffect(() => {
    if (!emblaApi || paused) return;
    autoplayRef.current = setInterval(() => {
      if (emblaApi.canScrollNext()) emblaApi.scrollNext();
      else emblaApi.scrollTo(0);
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(autoplayRef.current);
  }, [emblaApi, paused]);

  if (!photos || photos.length === 0) return null;

  return (
    <section data-testid="photo-carousel-section" className="py-20 sm:py-28 px-4 sm:px-6 bg-[#0A0A0A]">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
            {lang === "nl" ? "Sfeerbeelden" : "Impressions"}
          </h2>
          <button
            data-testid="carousel-pause-button"
            onClick={() => setPaused((p) => !p)}
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-gold border border-white/10 rounded-full px-4 py-2 transition-colors"
          >
            {paused ? <Play size={14} /> : <Pause size={14} />}
            {paused ? (lang === "nl" ? "Afspelen" : "Play") : (lang === "nl" ? "Pauzeren" : "Pause")}
          </button>
        </div>
        <Carousel setApi={setEmblaApi} opts={CAROUSEL_OPTS} className="w-full">
          <CarouselContent>
            {photos.map((p) => (
              <CarouselItem key={p.id} data-testid={`carousel-slide-${p.id}`} className="sm:basis-1/2 lg:basis-2/5">
                <div className="aspect-[4/5] overflow-hidden rounded-xl border border-white/5">
                  <img
                    src={`${BACKEND_URL}${p.url}`}
                    alt={(lang === "nl" ? p.alt_text_nl : p.alt_text_en) || "EliteTheBarber"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious data-testid="carousel-prev-button" className="bg-[#141414] border-white/20 text-white hover:bg-gold hover:text-white" />
          <CarouselNext data-testid="carousel-next-button" className="bg-[#141414] border-white/20 text-white hover:bg-gold hover:text-white" />
        </Carousel>
      </div>
    </section>
  );
};
